import { type Database } from '@databases/model/database';
import type { Field, FieldType } from '@databases/model/field';
import { type Value } from '@databases/model/value';
import { type View } from '@databases/model/views/view';
import { type CollabFilter, CollabFilterType } from '@databases/model/views/view-filter';
import { type CollabViewSortType } from '@databases/model/views/view-sort';
import { unparse } from 'papaparse';

import { encodeDatabaseValue } from './contents';
import { type Warnings } from './warnings';

function serializeSort(
  collabSort: CollabViewSortType,
  fieldsById: Record<string, Field>
) {
  const fieldName = getFieldName(collabSort.fieldId, fieldsById);
  return `${fieldName} ${collabSort.ascending ? 'ASC' : 'DESC'}`;
}

function serializeFilterValue(
  value: Value<FieldType> | undefined,
  field: Field
): string | undefined {
  if (!value) {
    return '';
  }

  const encoded = encodeDatabaseValue(value, field);
  if (encoded === undefined) {
    return undefined;
  }

  return encoded;
}

function getFieldName(fieldId: string, fieldsById: Record<string, Field>): string {
  const field = fieldsById[fieldId];
  if (!field) {
    throw new Error(`Field ${fieldId} not found`);
  }
  return field.name;
}

function serializeFilter(
  collabFilter: CollabFilter,
  fieldsById: Record<string, Field>,
  wrapInBrackets = true,
  warnings: Warnings
): string | undefined {
  let output = '';

  if (wrapInBrackets) {
    output += '[';
  }

  switch (collabFilter.type) {
    case CollabFilterType.EQUALS: {
      if (!(collabFilter.fieldId in fieldsById)) {
        break;
      }

      const field = fieldsById[collabFilter.fieldId];
      const fieldName = getFieldName(collabFilter.fieldId, fieldsById);
      const value = serializeFilterValue(collabFilter.getValue(), field);

      if (value === undefined) {
        warnings.add(`Value type for field ${collabFilter.fieldId} is unsupported`);
        return undefined;
      }

      const not = collabFilter.not ? 'not ' : '';

      output += `${fieldName} is ${not}${value}`;
      break;
    }
    case CollabFilterType.CONTAINS: {
      const fieldName = getFieldName(collabFilter.fieldId, fieldsById);
      const value = collabFilter.value;

      if (collabFilter.not) {
        output += `${fieldName} does not contain "${value}"`;
      } else {
        output += `${fieldName} contains "${value}"`;
      }
      break;
    }
    case CollabFilterType.EMPTY: {
      const fieldName = getFieldName(collabFilter.fieldId, fieldsById);
      const not = collabFilter.not ? 'not ' : '';

      output += `${fieldName} is ${not}empty`;

      break;
    }
    case CollabFilterType.AND: {
      const filters = collabFilter.value
        .map((e) => serializeFilter(e, fieldsById, false, warnings))
        .filter((e) => e !== undefined)
        .map((e) => `${e}`);

      output += filters.join(', ');
      break;
    }
    case CollabFilterType.OR: {
      const filters = collabFilter.value
        .map((e) => serializeFilter(e, fieldsById, false, warnings))
        .filter((e) => e !== undefined)
        .map((e) => `${e}`);

      output += filters.join('][');
      break;
    }
  }

  if (wrapInBrackets) {
    output += ']';
  }

  return output;
}

function serializeHiddenFields(
  view: View,
  fieldsById: Record<string, Field>
): string | undefined {
  const viewFields = view.fields;
  if (!viewFields) {
    return undefined;
  }

  const hiddenFieldNames = Object.entries(viewFields)
    .filter(([, isVisible]) => isVisible === false)
    .map(([fieldId]) => fieldsById[fieldId]?.name)
    .filter((name): name is string => name !== undefined);

  if (hiddenFieldNames.length === 0) {
    return undefined;
  }

  return `[${hiddenFieldNames.map((name) => `'${name}'`).join(',')}]`;
}

function serializeLayout(view: View, fieldsById: Record<string, Field>): string {
  if (view.layoutType === 'board') {
    const groupFieldId = view.grouping?.groupBy?.fieldId ?? view.groupFieldId;
    if (groupFieldId) {
      const groupFieldName = getFieldName(groupFieldId, fieldsById);
      return `board:${groupFieldName}`;
    }
  }
  return view.layoutType;
}

function serializeDatabaseView(
  view: View,
  viewId: string,
  fieldsById: Record<string, Field>,
  warnings: Warnings,
  defaultViewId: string,
  currentViewId?: string
) {
  const sorts = view.sorts
    .toArray()
    .map((e) => serializeSort(e, fieldsById))
    .join(',');

  const filters = view.filter
    ? serializeFilter(view.filter, fieldsById, true, warnings)
    : undefined;

  const hiddenFields = serializeHiddenFields(view, fieldsById);

  const isDefault = viewId === defaultViewId;
  const isCurrent = currentViewId ? viewId === currentViewId : isDefault;

  return {
    view_name: view.name,
    layout: serializeLayout(view, fieldsById),
    filters: filters ? `${filters}` : undefined,
    sorts: sorts.length > 0 ? `[${sorts}]` : undefined,
    hidden_fields: hiddenFields,
    is_default: isDefault || undefined,
    is_current: isCurrent || undefined,
  };
}

/**
 * Serializes the views of a database, including the filters, grouping and sorting.
 *
 * This will serialize as a CSV with the following format:
 *
 * view_name,layout,filters,sorts,hidden_fields,is_default,is_current
 * View 1,table,[Filter 1],[Sort 1],"['Email','University']",true,false
 * View 2,table,[Filter 2],[Sort 2],,false,true
 *
 * @param database The database to serialize the views of.
 * @param currentViewId Optional ID of the currently active view
 * @returns The serialized views.
 */
export function serializeDatabaseViews(
  database: Pick<Database, 'views' | 'viewsById' | 'fieldsById' | 'defaultViewId'>,
  warnings: Warnings,
  currentViewId?: string
): string {
  const effectiveDefaultViewId =
    database.defaultViewId && database.views.includes(database.defaultViewId)
      ? database.defaultViewId
      : database.views[0];

  const databaseViews = database.views
    .map((viewId) =>
      serializeDatabaseView(
        database.viewsById[viewId],
        viewId,
        database.fieldsById,
        warnings,
        effectiveDefaultViewId,
        currentViewId
      )
    )
    .filter((view) => view !== undefined);

  if (databaseViews.length === 0) {
    return 'view_name,layout,filters,sorts,hidden_fields,is_default,is_current\r\n';
  }

  return unparse(databaseViews, {
    header: true,
    escapeChar: '\\',
    columns: [
      'view_name',
      'layout',
      'filters',
      'sorts',
      'hidden_fields',
      'is_default',
      'is_current',
    ],
  });
}
